import React, { useRef, useState, useEffect } from 'react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@rocco/components/accordion';

/**
 * Interface representing a single FAQ element with title, question and answer
 * @interface FAQElement
 * @property {string} content - The full content of the FAQ
 * @property {string} title - The title/heading of the FAQ
 * @property {string} question - The FAQ question text
 * @property {string} answer - The FAQ answer text
 */
interface FAQElement {
    content: string;
    title: string;
    question?: string;
    answer?: string;
}

/**
 * Props interface for the CategoryPageFAQ component
 * @interface CategoryPageFAQProps
 * @property {FAQElement[]} faqSchemas - Array of FAQ items to display
 * @property {string} footerContent - HTML content to display in the header/footer section
 * @property {'accordion' | 'grid'} [variant='accordion'] - Display variant
 * @property {string} [categoryLabel] - Category label for grid variant header
 */
interface CategoryPageFAQProps {
    faqSchemas: FAQElement[];
    footerContent: string;
    variant?: 'accordion' | 'grid';
    categoryLabel?: string;
}

/**
 * Interface for parsed FAQ content from HTML
 */
interface ParsedFAQContent {
    introHtml: string;
    faqItems: Array<{
        title: string;
        content: string;
    }>;
}

/**
 * Parses HTML content to extract intro paragraphs and FAQ sections
 * FAQ sections are identified by h2/h3 headings with following content
 * @param html - The HTML string to parse
 * @returns Parsed content with intro and FAQ items
 */
function parseFooterContentToFAQ(html: string): ParsedFAQContent {
    // Return empty on server or if no html
    if (!html || typeof window === 'undefined') {
        return { introHtml: '', faqItems: [] };
    }

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const body = doc.body;

        let introHtml = '';
        const faqItems: Array<{ title: string; contentParts: string[] }> = [];

        let currentFAQ: { title: string; contentParts: string[] } | null = null;
        let hasFoundFirstParagraph = false;
        let hasFoundFirstHeading = false;

        // Iterate through all child elements
        Array.from(body.children).forEach((element) => {
            const tagName = element.tagName.toLowerCase();

            // Check if this is a heading (h2 or h3)
            if (tagName === 'h2' || tagName === 'h3') {
                hasFoundFirstHeading = true;

                // Save previous FAQ if exists and has content
                if (currentFAQ && currentFAQ.contentParts.length > 0) {
                    faqItems.push(currentFAQ);
                }

                // Start new FAQ section
                currentFAQ = {
                    title: element.textContent || '',
                    contentParts: [],
                };
            } else if (tagName === 'p' && !hasFoundFirstParagraph) {
                // First paragraph becomes the hero intro
                hasFoundFirstParagraph = true;
                introHtml = element.outerHTML;
            } else if (tagName === 'p' && !hasFoundFirstHeading) {
                // Subsequent paragraphs before first heading become FAQ items
                // with the paragraph text as the title (styled like questions)
                faqItems.push({
                    title: element.textContent || '',
                    contentParts: [],
                });
            } else {
                // Content after a heading goes to current FAQ
                if (currentFAQ) {
                    currentFAQ.contentParts.push(element.outerHTML);
                }
            }
        });

        // Don't forget the last FAQ section (only if it has content)
        if (currentFAQ !== null) {
            const lastFAQ = currentFAQ as {
                title: string;
                contentParts: string[];
            };
            if (lastFAQ.contentParts.length > 0) {
                faqItems.push(lastFAQ);
            }
        }

        return {
            introHtml,
            faqItems: faqItems.map((item) => ({
                title: item.title,
                content: item.contentParts.join(''),
            })),
        };
    } catch (error) {
        console.error('Failed to parse footer content:', error);
        return { introHtml: '', faqItems: [] };
    }
}

/**
 * Grid variant component for static FAQ display (used on products pages)
 * Parses footerContent HTML to extract intro text and FAQ items
 * Displays FAQs in a newspaper-style 3-column layout
 */
const FAQGridVariant = ({
    footerContent,
    categoryLabel,
}: {
    footerContent: string;
    categoryLabel?: string;
}) => {
    // Use useState + useEffect for client-side only parsing (SSR safe)
    const [parsedContent, setParsedContent] = useState<ParsedFAQContent>({
        introHtml: '',
        faqItems: [],
    });

    useEffect(() => {
        if (footerContent) {
            setParsedContent(parseFooterContentToFAQ(footerContent));
        }
    }, [footerContent]);

    const { introHtml, faqItems } = parsedContent;

    // Don't render if no content (but show during SSR with empty state)
    if (!introHtml && faqItems.length === 0 && !categoryLabel) {
        return null;
    }

    return (
        <div className="flex flex-col gap-14.5 px-6 md:px-12">
            {/* Header Section - category label and hero intro */}
            {(categoryLabel || introHtml) && (
                <div className="flex flex-col gap-11">
                    {categoryLabel && (
                        <div className="border-t border-gray-200 pt-4">
                            <span className="text-4 font-normal tracking-[-0.03em] text-[#323232]">
                                {categoryLabel}
                            </span>
                        </div>
                    )}
                    {introHtml && (
                        <div
                            className="faq-grid-intro font-twk-ghost text-[2.15rem] font-normal leading-[1.4] tracking-[-0.025em] text-black md:max-w-370"
                            dangerouslySetInnerHTML={{ __html: introHtml }}
                        />
                    )}
                </div>
            )}

            {/* FAQ Columns - newspaper-style layout flowing down then across */}
            {faqItems.length > 0 && (
                <div className="columns-1 gap-21.5 md:max-w-370 md:columns-3">
                    {faqItems.map((faq, index) => (
                        <div
                            key={`faq-grid-${index}`}
                            className="mb-10 flex break-inside-avoid flex-col gap-1.5 md:max-w-109"
                        >
                            <h3 className="text-3.5 font-semibold leading-normal tracking-[0.02em] text-[#141414]">
                                {faq.title}
                            </h3>
                            {faq.content && (
                                <div
                                    className="faq-grid-answer text-3.5 font-normal leading-[1.4] tracking-[0.02em] text-[#4b4b4b]"
                                    dangerouslySetInnerHTML={{
                                        __html: faq.content,
                                    }}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
            <div className="border-t border-gray-200 pt-4" />
        </div>
    );
};

/**
 * Component that renders an FAQ section with expandable accordion items
 * or as a static grid layout depending on the variant prop
 * @param {CategoryPageFAQProps} props - Component props
 * @returns {JSX.Element | null} The rendered FAQ section or null if no content
 */
export const CategoryPageFAQ = (props: CategoryPageFAQProps) => {
    const {
        faqSchemas,
        footerContent,
        variant = 'accordion',
        categoryLabel,
    } = props;
    const openedItemRef = useRef<HTMLDivElement>(null);

    // For accordion variant, filter faqSchemas
    const filteredFAQSchemas = React.useMemo(
        () => faqSchemas.filter((faq) => faq.question && faq.answer),
        [faqSchemas],
    );

    // Grid variant - parses footerContent to extract FAQ items
    if (variant === 'grid') {
        if (!footerContent) {
            return null;
        }
        return (
            <FAQGridVariant
                footerContent={footerContent}
                categoryLabel={categoryLabel}
            />
        );
    }

    // Early return if no content for accordion variant
    if (filteredFAQSchemas.length === 0 && !footerContent) {
        return null;
    }

    // Default accordion variant (existing behavior)
    const defaultOpen = footerContent
        ? ['footerContent']
        : filteredFAQSchemas[0]?.question
          ? [filteredFAQSchemas[0]?.question]
          : [];

    return (
        <div className="mb-16 px-6 md:px-12">
            <div className="flex flex-col gap-12">
                {/* Footer content */}
                {footerContent && (
                    <div
                        className="faq-footer"
                        dangerouslySetInnerHTML={{ __html: footerContent }}
                    />
                )}

                {/* Render FAQs only if present */}
                {filteredFAQSchemas.length > 0 && (
                    <div>
                        <h4 className="faq-footer-title">FAQs</h4>
                        <Accordion type="multiple" defaultValue={defaultOpen}>
                            {filteredFAQSchemas.map((faq) => (
                                <AccordionItem
                                    key={faq.title}
                                    value={faq.question || ''}
                                >
                                    <div ref={openedItemRef}>
                                        <AccordionTrigger
                                            variant="narrow"
                                            icon="plus"
                                            openIcon="minus"
                                        >
                                            <div className="flex w-full items-center justify-between">
                                                <span
                                                    className="text-left text-xl font-medium"
                                                    dangerouslySetInnerHTML={{
                                                        __html: faq.title,
                                                    }}
                                                />
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div
                                                className="grid py-2"
                                                dangerouslySetInnerHTML={{
                                                    __html: faq.answer || '',
                                                }}
                                            />
                                        </AccordionContent>
                                    </div>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                )}
            </div>
        </div>
    );
};
